import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { useMutation } from "react-query";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DEFAULT_PERCENT = 0.08333; // 1/12 of year

function AllocationProfileDialog({
  open,
  onClose,
  onSave,
  profile,
  apiBaseUrl,
}) {
  const { getAccessTokenSilently } = useAuth0();
  const [profileName, setProfileName] = useState("");
  const [monthlyValues, setMonthlyValues] = useState([]);
  const [error, setError] = useState("");

  // Initialize form when dialog opens or profile changes
  useEffect(() => {
    if (open) {
      if (profile) {
        // Editing existing profile
        setProfileName(profile.profile_name);
        const items = profile.items || [];
        const values = [];
        for (let month = 1; month <= 12; month++) {
          const item = items.find((i) => i.month_num === month);
          values.push({
            month_num: month,
            percent_of_year: item
              ? parseFloat(item.percent_of_year)
              : DEFAULT_PERCENT,
          });
        }
        setMonthlyValues(values);
      } else {
        // Creating new profile
        setProfileName("");
        const values = [];
        for (let month = 1; month <= 12; month++) {
          values.push({
            month_num: month,
            percent_of_year: DEFAULT_PERCENT,
          });
        }
        setMonthlyValues(values);
      }
      setError("");
    }
  }, [open, profile]);

  // Create mutation
  const createMutation = useMutation(
    async (data) => {
      const token = await getAccessTokenSilently();
      const res = await axios.post(apiBaseUrl, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    },
    {
      onSuccess: () => {
        onSave();
      },
      onError: (error) => {
        setError(error.response?.data?.message || "Error creating profile");
      },
    }
  );

  // Update mutation
  const updateMutation = useMutation(
    async ({ profileNdx, data }) => {
      const token = await getAccessTokenSilently();
      const res = await axios.put(`${apiBaseUrl}/${profileNdx}`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    },
    {
      onSuccess: () => {
        onSave();
      },
      onError: (error) => {
        setError(error.response?.data?.message || "Error updating profile");
      },
    }
  );

  const handleMonthChange = (monthNum, value) => {
    const newValues = [...monthlyValues];
    const index = newValues.findIndex((v) => v.month_num === monthNum);
    if (index !== -1) {
      newValues[index].percent_of_year = parseFloat(value) || 0;
      setMonthlyValues(newValues);
    }
  };

  const handleDistributeEvenly = () => {
    const newValues = monthlyValues.map((v) => ({
      ...v,
      percent_of_year: DEFAULT_PERCENT,
    }));
    setMonthlyValues(newValues);
  };

  const calculateTotal = () => {
    return monthlyValues.reduce((sum, item) => sum + item.percent_of_year, 0);
  };

  const calculateQuarterTotal = (quarter) => {
    const startMonth = (quarter - 1) * 3 + 1;
    const endMonth = quarter * 3;
    return monthlyValues
      .filter(
        (item) => item.month_num >= startMonth && item.month_num <= endMonth
      )
      .reduce((sum, item) => sum + item.percent_of_year, 0);
  };

  const handleSave = async () => {
    setError("");

    if (!profileName.trim()) {
      setError("Profile name is required");
      return;
    }

    const total = calculateTotal();
    if (Math.abs(total - 1.0) > 0.001) {
      setError(
        `Total must equal 100% (currently ${(total * 100).toFixed(2)}%)`
      );
      return;
    }

    const data = {
      profile_name: profileName,
      items: monthlyValues,
      is_active: profile?.is_active !== false,
    };

    try {
      if (profile) {
        await updateMutation.mutateAsync({
          profileNdx: profile.profile_ndx,
          data,
        });
      } else {
        await createMutation.mutateAsync(data);
      }
    } catch (err) {
      // Error is handled in mutation onError
    }
  };

  const total = calculateTotal();
  const totalPercent = (total * 100).toFixed(2);
  const isValidTotal = Math.abs(total - 1.0) <= 0.001;

  const renderQuarterRow = (quarter) => {
    const quarterTotal = calculateQuarterTotal(quarter);
    const quarterPercent = (quarterTotal * 100).toFixed(2);
    const quarterName = `Q${quarter} Total`;

    return (
      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
        <TableCell sx={{ fontWeight: "bold", pl: 2 }}>{quarterName}</TableCell>
        <TableCell align="right" sx={{ fontWeight: "bold" }}>
          {quarterTotal.toFixed(5)}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: "bold" }}>
          {quarterPercent}%
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {profile ? "Edit Allocation Profile" : "Create New Allocation Profile"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <TextField
            label="Profile Name"
            value={profileName}
            onChange={(e) => setProfileName(e.target.value)}
            fullWidth
            margin="normal"
            required
          />

          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Monthly Allocations
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={handleDistributeEvenly}
              sx={{ mb: 2 }}
            >
              Distribute Evenly (8.33% each)
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#e0e0e0" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>Month</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    Percent of Year
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    Percentage (%)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {monthlyValues.map((item) => (
                  <React.Fragment key={item.month_num}>
                    <TableRow>
                      <TableCell>{MONTH_NAMES[item.month_num - 1]}</TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          value={item.percent_of_year}
                          onChange={(e) =>
                            handleMonthChange(item.month_num, e.target.value)
                          }
                          inputProps={{
                            step: 0.00001,
                            min: 0,
                            max: 1,
                          }}
                          size="small"
                          sx={{ width: 120 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {(item.percent_of_year * 100).toFixed(2)}%
                      </TableCell>
                    </TableRow>
                    {/* Add quarter total rows after March, June, September, and December */}
                    {item.month_num === 3 && renderQuarterRow(1)}
                    {item.month_num === 6 && renderQuarterRow(2)}
                    {item.month_num === 9 && renderQuarterRow(3)}
                    {item.month_num === 12 && renderQuarterRow(4)}
                  </React.Fragment>
                ))}
                <TableRow sx={{ backgroundColor: "#e8f5e9" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Annual Total
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    {total.toFixed(5)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      color: isValidTotal ? "success.main" : "error.main",
                    }}
                  >
                    {totalPercent}%
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {!isValidTotal && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Total must equal 100%. Currently: {totalPercent}%
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!isValidTotal || !profileName.trim()}
        >
          {profile ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AllocationProfileDialog;
